import { useAuth0 } from '@auth0/auth0-react';

export function UseAuthRoles() {
  const { user, isAuthenticated } = useAuth0();
  const namespace = 'https://inkedupmanager.com';
  
  // Get app_metadata for subscription details (secondary source)
  const appMetadata = isAuthenticated ? (user?.[`${namespace}/app_metadata`] || {}) : {};
  
  // Get roles from the token (primary source - most current)
  const roles = isAuthenticated ? (user?.[`${namespace}/roles`] || []) : [];
  
  // Function to check if user has a specific role
  const hasRole = (requiredRole) => {
    return roles.includes(requiredRole);
  };
  
  // Function to check if user has any of the required roles
  const hasAnyRole = (requiredRoles) => {
    return requiredRoles.some(role => roles.includes(role));
  };
  
  // Get subscription level - PRIMARY SOURCE: JWT roles (most current)
  const getSubscriptionLevel = () => {
    // First check JWT roles (most current and reliable)
    if (hasRole('growth_studio_subscriber')) return 'growth_studio';
    if (hasRole('starter_studio_subscriber')) return 'starter_studio';
    if (hasRole('growth_studio_member')) return 'growth_studio'; // Add studio member roles
    if (hasRole('starter_studio_member')) return 'starter_studio'; // Add studio member roles
    if (hasRole('standard_subscriber')) return 'standard';
    if (hasRole('basic_subscriber')) return 'basic';
    
    // Fallback to app_metadata if roles are empty
    const subscriptionPlan = appMetadata.subscription_plan;
    if (subscriptionPlan) {
      const planToTier = {
        'growth_studio_subscriber': 'growth_studio',
        'starter_studio_subscriber': 'starter_studio',
        'growth_studio_member': 'growth_studio',
        'starter_studio_member': 'starter_studio',
        'standard_subscriber': 'standard',
        'basic_subscriber': 'basic'
      };
      return planToTier[subscriptionPlan] || 'unpaid';
    }
    
    return 'unpaid';
  };
  
  // Get subscription details
  const getSubscriptionDetails = () => {
    const tier = getSubscriptionLevel();
    return {
      tier,
      customerId: appMetadata.stripe_customer_id || null,
      subscriptionId: appMetadata.stripe_subscription_id || null,
      hasActiveSubscription: appMetadata.has_subscription || false,
      lastUpdated: appMetadata.updated_at || null,
      isStudioPlan: ['starter_studio', 'growth_studio'].includes(tier)
    };
  };
  
  // Check if user has access to specific subscription tier or higher
  const hasSubscriptionAccess = (requiredTier) => {
    const tierHierarchy = ['unpaid', 'basic', 'standard', 'starter_studio', 'growth_studio'];
    const currentTier = getSubscriptionLevel();
    const currentTierIndex = tierHierarchy.indexOf(currentTier);
    const requiredTierIndex = tierHierarchy.indexOf(requiredTier);
    return currentTierIndex >= requiredTierIndex;
  };
  
  // Check if user has access to aftercare email feature
  const hasAftercareEmailAccess = () => {
    const tier = getSubscriptionLevel();
    return ['standard', 'growth_studio'].includes(tier);
  };
  
  // Check if user has studio management access
  const hasStudioAccess = () => {
    const tier = getSubscriptionLevel();
    return ['starter_studio', 'growth_studio'].includes(tier);
  };
  
  // Check if user can add additional seats (only owners, not studio members)
  const canAddSeats = () => {
    return hasRole('starter_studio_subscriber') || hasRole('growth_studio_subscriber');
  };
  
  // Check if user is a studio member (not owner)
  const isStudioMember = () => {
    return hasRole('starter_studio_member') || hasRole('growth_studio_member');
  };
  
  // Check if user is a studio owner
  const isStudioOwner = () => {
    return hasRole('starter_studio_subscriber') || hasRole('growth_studio_subscriber');
  };

  // ===== NEW: PUBLIC PROFILE ACCESS METHODS =====
  
  // Check if user has access to public profile features (Growth Studio only)
  const hasPublicProfileAccess = () => {
    return hasRole('growth_studio_subscriber') || hasRole('growth_studio_member');
  };

  // Check if user can manage portfolio (Growth Studio only)
  const hasPortfolioAccess = () => {
    return hasRole('growth_studio_subscriber') || hasRole('growth_studio_member');
  };

  // Check if user can access contact inquiries (Growth Studio only)
  const hasContactInquiriesAccess = () => {
    return hasRole('growth_studio_subscriber') || hasRole('growth_studio_member');
  };

  // Check if user can configure public profile settings (Growth Studio only)
  const hasPublicProfileSettingsAccess = () => {
    return hasRole('growth_studio_subscriber') || hasRole('growth_studio_member');
  };

  // Check if user has access to public profile preview (Growth Studio only)
  const hasPublicProfilePreviewAccess = () => {
    return hasRole('growth_studio_subscriber') || hasRole('growth_studio_member');
  };

  // ===== ENHANCED FEATURE ACCESS METHODS =====
  
  // Check if user has access to advanced reports (Standard+ and Studio plans)
  const hasAdvancedReportsAccess = () => {
    const tier = getSubscriptionLevel();
    return ['standard', 'starter_studio', 'growth_studio'].includes(tier);
  };

  // Check if user has access to custom forms (Standard+ and Studio plans)
  const hasCustomFormsAccess = () => {
    const tier = getSubscriptionLevel();
    return ['standard', 'starter_studio', 'growth_studio'].includes(tier);
  };

  // Check if user has access to studio analytics (Studio plans only)
  const hasStudioAnalyticsAccess = () => {
    const tier = getSubscriptionLevel();
    return ['starter_studio', 'growth_studio'].includes(tier);
  };

  // Check if user has access to custom integrations (Growth Studio subscribers only)
  const hasCustomIntegrationsAccess = () => {
    return hasRole('growth_studio_subscriber'); // Only owners, not members
  };

  // Check if user has access to white label features (Growth Studio subscribers only)
  const hasWhiteLabelAccess = () => {
    return hasRole('growth_studio_subscriber'); // Only owners, not members
  };

  // Check if user has billing access (Studio owners only)
  const hasStudioBillingAccess = () => {
    return hasRole('starter_studio_subscriber') || hasRole('growth_studio_subscriber');
  };

  // ===== COMPREHENSIVE FEATURE MAPPING =====
  
  // Get all available features for current user
  const getAvailableFeatures = () => {
    const tier = getSubscriptionLevel();
    const features = {
      // Basic features (all paid users)
      basicClientManagement: tier !== 'unpaid',
      basicAppointments: tier !== 'unpaid',
      basicInventory: tier !== 'unpaid',
      basicConsentForms: tier !== 'unpaid',
      
      // Standard features
      advancedReports: hasAdvancedReportsAccess(),
      customForms: hasCustomFormsAccess(),
      aftercareEmails: hasAftercareEmailAccess(),
      
      // Studio features
      studioManagement: hasStudioAccess(),
      studioAnalytics: hasStudioAnalyticsAccess(),
      teamManagement: hasStudioAccess(),
      
      // Growth Studio exclusive features
      publicProfiles: hasPublicProfileAccess(),
      portfolioManager: hasPortfolioAccess(),
      contactInquiries: hasContactInquiriesAccess(),
      customIntegrations: hasCustomIntegrationsAccess(),
      whiteLabelOptions: hasWhiteLabelAccess(),
      
      // Owner-only features
      studioBilling: hasStudioBillingAccess(),
      addSeats: canAddSeats()
    };
    
    return features;
  };

  // Check if user has access to specific feature by name
  const hasFeatureAccess = (featureName) => {
    const features = getAvailableFeatures();
    return features[featureName] || false;
  };

  // Get user type for display purposes
  const getUserType = () => {
    if (!isAuthenticated) return 'guest';
    if (getSubscriptionLevel() === 'unpaid') return 'unpaid';
    if (isStudioOwner()) return 'studio_owner';
    if (isStudioMember()) return 'studio_member';
    return 'individual_subscriber';
  };

  // Get subscription tier display name
  const getSubscriptionDisplayName = () => {
    const tier = getSubscriptionLevel();
    const displayNames = {
      'unpaid': 'Free Trial',
      'basic': 'Basic Plan',
      'standard': 'Standard Plan',
      'starter_studio': 'Starter Studio',
      'growth_studio': 'Growth Studio'
    };
    return displayNames[tier] || 'Unknown Plan';
  };

  return {
    // Original methods
    roles,
    appMetadata,
    hasRole,
    hasAnyRole,
    getSubscriptionLevel,
    getSubscriptionDetails,
    hasSubscriptionAccess,
    hasAftercareEmailAccess,
    hasStudioAccess,
    canAddSeats,
    isStudioMember,
    isStudioOwner,
    
    // Original boolean checks
    isUnpaidUser: getSubscriptionLevel() === 'unpaid',
    isBasicSubscriber: getSubscriptionLevel() === 'basic',
    isStandardSubscriber: getSubscriptionLevel() === 'standard',
    isStarterStudioSubscriber: getSubscriptionLevel() === 'starter_studio',
    isGrowthStudioSubscriber: getSubscriptionLevel() === 'growth_studio',
    isStudioUser: hasStudioAccess(),
    
    // NEW: Public profile access methods
    hasPublicProfileAccess,
    hasPortfolioAccess,
    hasContactInquiriesAccess,
    hasPublicProfileSettingsAccess,
    hasPublicProfilePreviewAccess,
    
    // NEW: Enhanced feature access methods
    hasAdvancedReportsAccess,
    hasCustomFormsAccess,
    hasStudioAnalyticsAccess,
    hasCustomIntegrationsAccess,
    hasWhiteLabelAccess,
    hasStudioBillingAccess,
    
    // NEW: Comprehensive feature management
    getAvailableFeatures,
    hasFeatureAccess,
    getUserType,
    getSubscriptionDisplayName
  };
}

export default UseAuthRoles;